
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { T } from "./adminTheme";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  SectionCard,
  StyledTableRow,
  TabHeader,
  BoolChip,
  AvailabilityChip,
  TableStateWrapper,
  FilterableSortableTableHead,
  useSortableTable,
  applyColumnFilters,
  RowActionButton,
} from "./shared";

import {
  checkWorkstationGlobalStatus,
  releaseWorkstationLockSafely,
} from "./recoveryHelpers";

// ✅ Optimized dialogs - prevents typing delay
import WorkstationCreateDialog from "./WorkstationCreateDialog";
import WorkstationEditDialog from "./WorkstationEditDialog";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLUMNS = [
  { id: "MachineCode", label: "Machine Code", minWidth: 120 },
  { id: "MachineDescription", label: "Description", minWidth: 160 },
  { id: "MachineGroupCode", label: "Group", minWidth: 100 },
  { id: "HoursType", label: "Hours Type", minWidth: 100 },
  { id: "FinishedGood", label: "FG", minWidth: 60 },
  { id: "active_status", label: "Status", minWidth: 120 },
  { id: "avaliability", label: "Availability", minWidth: 130 },
  { id: "activeUserEmpId", label: "Active User", minWidth: 140 },
  { id: "_actions", label: "Actions", sortable: false, align: "right", minWidth: 60 },
];

function getWorkstationRecommendationMessage(status) {
  if (!status) return "Unable to analyze workstation recovery status.";

  if (status.recommendation === "USE_MACHINE_HOUR_RECOVERY") {
    return "This workstation has active machine-hour records. Please use Session Recovery before direct release.";
  }

  if (status.recommendation === "USE_MAN_HOUR_RECOVERY") {
    return "This workstation has active man-hour records. Please use Man-hour Recovery before direct release.";
  }

  if (status.recommendation === "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") {
    return "This workstation has active records in both machine-hour and man-hour sections. Please check both recovery sections before direct release.";
  }

  if (status.recommendation === "DIRECT_RELEASE_ALLOWED") {
    return "No active machine-hour or man-hour records were found. Direct workstation release is allowed.";
  }

  return "No active recovery issue found.";
}

function getCount(value) {
  return Array.isArray(value) ? value.length : 0;
}

function WorkstationGlobalReleaseDialog({
  open,
  onClose,
  releaseDlg,
  onConfirmRelease,
  onGoToMachineRecovery,
  onGoToManhourRecovery,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const status = releaseDlg.status;
  const isDirectAllowed = Boolean(status?.canDirectRelease);
  const hasMachineHour = Boolean(status?.machineHour?.hasActiveRecords);
  const hasManHour = Boolean(status?.manHour?.hasActiveRecords);

  const machineOpenShiftCount = getCount(status?.machineHour?.openShifts);
  const machineOpenOpCount = getCount(status?.machineHour?.openOperations);
  const machineOpenJobCount = getCount(status?.machineHour?.openJobSummaries);
  const manOpenShiftCount = getCount(status?.manHour?.openShifts);
  const manOpenDetailCount = getCount(status?.manHour?.openDetails);

  const actionLabel = releaseDlg.intent === "reset_user"
    ? "Reset Active User / Release Lock"
    : "Release Workstation";

  return (
    <Dialog
      open={open}
      onClose={releaseDlg.actionLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDirectAllowed ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.14)",
              color: isDirectAllowed ? "success.main" : "warning.main",
            }}
          >
            {isDirectAllowed ? <CheckCircleIcon /> : <WarningAmberIcon />}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: t.heading }}>
              Workstation Recovery Check
            </Typography>
            <Typography sx={{ fontSize: 12, color: t.muted }}>
              Machine: {releaseDlg.machineCode || "—"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {releaseDlg.loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 5, gap: 1 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: 13, color: t.muted }}>
              Checking machine-hour and man-hour records...
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Alert severity={isDirectAllowed ? "success" : "warning"}>
              {getWorkstationRecommendationMessage(status)}
            </Alert>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                color={hasMachineHour ? "warning" : "success"}
                label={hasMachineHour ? "Machine-hour active" : "Machine-hour clean"}
              />
              <Chip
                size="small"
                color={hasManHour ? "warning" : "success"}
                label={hasManHour ? "Man-hour active" : "Man-hour clean"}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Action: ${releaseDlg.intent === "reset_user" ? "Reset active user" : "Set available"}`}
              />
            </Box>

            {status && (
              <Box
                sx={{
                  border: `1px solid ${t.border}`,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 2, py: 1.25, background: t.s1, borderBottom: `1px solid ${t.border}` }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.heading }}>
                    Active Record Summary
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.heading, mb: 0.75 }}>
                        Machine-hour
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`Open shifts: ${machineOpenShiftCount}`} />
                        <Chip size="small" label={`Open operations: ${machineOpenOpCount}`} />
                        <Chip size="small" label={`Open jobs: ${machineOpenJobCount}`} />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.heading, mb: 0.75 }}>
                        Man-hour
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`Open shifts: ${manOpenShiftCount}`} />
                        <Chip size="small" label={`Open details: ${manOpenDetailCount}`} />
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {!isDirectAllowed && (
              <Alert severity="info">
                Direct workstation release is blocked to prevent data mismatch. Use the recommended recovery section first, then retry this action.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={onClose}
          color="inherit"
          size="small"
          disabled={releaseDlg.actionLoading}
        >
          Close
        </Button>

        {!releaseDlg.loading && status?.recommendation !== "DIRECT_RELEASE_ALLOWED" && hasMachineHour && (
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<CleaningServicesIcon />}
            onClick={onGoToMachineRecovery}
          >
            Go to Session Recovery
          </Button>
        )}

        {!releaseDlg.loading && status?.recommendation !== "DIRECT_RELEASE_ALLOWED" && hasManHour && (
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<CleaningServicesIcon />}
            onClick={onGoToManhourRecovery}
          >
            Go to Man-hour Recovery
          </Button>
        )}

        {!releaseDlg.loading && isDirectAllowed && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={onConfirmRelease}
            disabled={releaseDlg.actionLoading}
            startIcon={releaseDlg.actionLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
          >
            {releaseDlg.actionLoading ? "Releasing..." : actionLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function WorkstationsTab({
  onSnack,
  goToSessionRecovery,
  goToManhourRecovery,
  adminUsername,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  // ✅ Removed form state - now in WorkstationCreateDialog (prevents typing delay)
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editWorkstation, setEditWorkstation] = useState(null);  // ✅ Changed from editForm

  const [releaseDlg, setReleaseDlg] = useState({
    open: false,
    machineCode: null,
    intent: null,
    loading: false,
    status: null,
    actionLoading: false,
  });

  // ✅ Removed h helper - now in dialog components

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/admin/machines`);
      setList(r.data || []);
      setColumnFilters({});
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to load workstations.", "error");
    } finally {
      setLoading(false);
    }
  }, [onSnack]);

  useEffect(() => {
    load();
  }, [load]);

  // ✅ Removed existingGroups and handleGroupSelect - now in WorkstationCreateDialog

  const save = async (formData) => {  // ✅ Now accepts form data from dialog
    if (!formData.MachineCode || !formData.MachineGroupCode || !formData.MachineDescription || !formData.HoursType) {
      onSnack("Please fill all required workstation fields.", "warning");
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${API_BASE_URL}/admin/createMachine`, formData);  // ✅ Use formData
      onSnack("Workstation created successfully.");
      setOpenCreate(false);
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to create workstation.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateMachineStatus = async (machineCode, active_status) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/machines/${machineCode}/status`, {
        active_status,
      });
      onSnack("Machine status updated.");
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update machine status.", "error");
    }
  };

  const updateMachineAvailability = async (machineCode, avaliability) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/machines/${machineCode}/availability`, {
        avaliability,
      });
      onSnack("Machine availability updated.");
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update machine availability.", "error");
    }
  };

  const saveEdit = async (formData) => {  // ✅ Now accepts form data from dialog
    if (!formData) return;

    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/admin/machines/${formData.MachineCode}`, {
        MachineGroupCode: formData.MachineGroupCode,
        GroupDescription: formData.GroupDescription,
        MachineDescription: formData.MachineDescription,
        HoursType: formData.HoursType,
        FinishedGood: formData.FinishedGood,
        active_status: formData.active_status,
      });

      onSnack("Workstation updated successfully.");
      setOpenEdit(false);
      setEditWorkstation(null);  // ✅ Changed from setEditForm
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update workstation.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openReleaseDialog = async (machineCode, intent) => {
    setReleaseDlg({
      open: true,
      machineCode,
      intent,
      loading: true,
      status: null,
      actionLoading: false,
    });

    try {
      const status = await checkWorkstationGlobalStatus(machineCode);

      setReleaseDlg({
        open: true,
        machineCode,
        intent,
        loading: false,
        status,
        actionLoading: false,
      });
    } catch (err) {
      setReleaseDlg({
        open: false,
        machineCode: null,
        intent: null,
        loading: false,
        status: null,
        actionLoading: false,
      });

      onSnack(
        err?.response?.data?.sqlMessage ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to analyze workstation global status.",
        "error"
      );
    }
  };

  const closeReleaseDialog = () => {
    if (releaseDlg.actionLoading) return;

    setReleaseDlg({
      open: false,
      machineCode: null,
      intent: null,
      loading: false,
      status: null,
      actionLoading: false,
    });
  };

  const confirmReleaseAction = async () => {
    const { machineCode, intent, status } = releaseDlg;

    if (!status?.canDirectRelease) {
      onSnack("Direct release is blocked. Please use the recommended recovery section first.", "warning");
      return;
    }

    setReleaseDlg((prev) => ({ ...prev, actionLoading: true }));

    try {
      await releaseWorkstationLockSafely({
        machineCode,
        cleanedBy: adminUsername || "admin",
        reason:
          intent === "reset_user"
            ? "Manual workstation active user reset from Admin Workstations tab after global machine-hour/man-hour check"
            : "Manual workstation release from Admin Workstations tab after global machine-hour/man-hour check",
      });

      onSnack("Workstation released successfully.", "success");

      setReleaseDlg({
        open: false,
        machineCode: null,
        intent: null,
        loading: false,
        status: null,
        actionLoading: false,
      });

      load();
    } catch (err) {
      onSnack(
        err?.response?.data?.sqlMessage ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Workstation release failed.",
        "error"
      );

      setReleaseDlg((prev) => ({ ...prev, actionLoading: false }));
    }
  };

  const goToMachineRecovery = () => {
    const { machineCode } = releaseDlg;
    closeReleaseDialog();

    if (typeof goToSessionRecovery === "function") {
      goToSessionRecovery({ machineCode });
      return;
    }

    onSnack(`Open Session Recovery and search Machine Code ${machineCode}.`, "info");
  };


const goToManhourRecoverySection = () => {
  const { machineCode, status } = releaseDlg;  // ✅ CORRECT: Use "status"
  


  const firstOpenShift = status?.manHour?.openShifts?.[0];  // ✅ Use status
  const supervisorId =
    firstOpenShift?.supervisor_id ||
    status?.machine?.activeUserEmpId ||  // ✅ Use status
    "";


  closeReleaseDialog();

  if (typeof goToManhourRecovery === "function") { 
    goToManhourRecovery({
      stationCode: machineCode,
      supervisorId,
    });
    return;
  }

  onSnack(`Open Man-hour Recovery and search Station Code ${machineCode}.`, "info");
};



  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();

    return list.filter((m) => (
      m.MachineCode?.toLowerCase().includes(q) ||
      m.MachineDescription?.toLowerCase().includes(q) ||
      m.MachineGroupCode?.toLowerCase().includes(q) ||
      m.GroupDescription?.toLowerCase().includes(q) ||
      m.HoursType?.toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const {
    sortedRows: rows,
    sortField,
    sortDir,
    handleSort,
  } = useSortableTable(columnFilteredRows, "MachineCode");

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="Workstations"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            onAdd={() => {
              setOpenCreate(true);  // ✅ Dialog handles its own form reset
            }}
            btnLabel="Create Workstation"
            btnColor="linear-gradient(90deg,#3b82f6,#2563eb)"
          />

          <Divider />

          <TableStateWrapper
            loading={loading}
            empty={rows.length === 0}
            emptyMessage={search ? "No workstations match your search." : "No workstations found."}
            colSpan={COLUMNS.length}
            minHeight={360}
            skeletonRows={7}
          >
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />

                <TableBody>
                  {rows.map((m) => {
                    const isEnabled = Number(m.active_status) === 1;
                    const isAvailable = Number(m.avaliability) === 1;

                    return (
                      <StyledTableRow key={m.MachineCode}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, color: t.blue, fontFamily: "monospace" }}>
                            {m.MachineCode}
                          </Typography>
                        </TableCell>

                        <TableCell>{m.MachineDescription}</TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            {m.MachineGroupCode}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: t.muted }}>
                            {m.GroupDescription}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={m.HoursType === "Man" ? "Man Hours" : "Machine Hours"}
                            color={m.HoursType === "Man" ? "secondary" : "primary"}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell>
                          <BoolChip value={m.FinishedGood} trueLabel="FG" falseLabel="Non-FG" />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <BoolChip value={m.active_status} trueLabel="Enabled" falseLabel="Disabled" />
                            <Box sx={{
                              opacity: 0,
                              ".MuiTableRow-root:hover &": { opacity: 1 },
                              transition: "opacity 0.15s ease",
                            }}>
                              <RowActionButton
                                title={isEnabled ? "Disable workstation" : "Enable workstation"}
                                icon={<PowerSettingsNewIcon />}
                                color={isEnabled ? "error" : "success"}
                                onClick={() => updateMachineStatus(m.MachineCode, isEnabled ? 0 : 1)}
                              />
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <AvailabilityChip value={m.avaliability} />
                            <Box sx={{
                              opacity: 0,
                              ".MuiTableRow-root:hover &": { opacity: 1 },
                              transition: "opacity 0.15s ease",
                            }}>
                              <RowActionButton
                                title={isAvailable ? "Mark as In Use" : "Set as Available (global session check)"}
                                icon={<SwapHorizIcon />}
                                color={isAvailable ? "warning" : "success"}
                                onClick={() => {
                                  if (isAvailable) {
                                    updateMachineAvailability(m.MachineCode, 0);
                                  } else {
                                    openReleaseDialog(m.MachineCode, "available");
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography sx={{
                              fontSize: 12,
                              fontFamily: "monospace",
                              fontWeight: m.activeUserEmpId ? 600 : 400,
                              color: m.activeUserEmpId ? t.primary : t.muted,
                            }}>
                              {m.activeUserEmpId ?? "—"}
                            </Typography>
                            {m.activeUserEmpId && (
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title="Reset active user (global session check)"
                                  icon={<LinkOffIcon />}
                                  color="warning"
                                  onClick={() => openReleaseDialog(m.MachineCode, "reset_user")}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          <RowActionButton
                            title="Edit workstation"
                            icon={<EditIcon />}
                            color="primary"
                            onClick={() => {
                              setEditWorkstation({ ...m });  // ✅ Changed from setEditForm
                              setOpenEdit(true);
                            }}
                          />
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </SectionCard>

        {/* ✅ Optimized create dialog - no typing delay */}
        <WorkstationCreateDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSave={save}
          saving={saving}
          machines={list}
        />

        {/* ✅ Optimized edit dialog - no typing delay */}
        <WorkstationEditDialog
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          onSave={saveEdit}
          saving={saving}
          workstation={editWorkstation}
        />

        <WorkstationGlobalReleaseDialog
          open={releaseDlg.open}
          onClose={closeReleaseDialog}
          releaseDlg={releaseDlg}
          onConfirmRelease={confirmReleaseAction}
          onGoToMachineRecovery={goToMachineRecovery}
          onGoToManhourRecovery={goToManhourRecoverySection}
        />
      </Box>
    </Fade>
  );
}